// infrastructure/mappers/targetMapper.js
import { createTarget } from '../../domain/model/Target';

/**
 * Where the stored `programTargets` shape and the Target model meet.
 *
 * NOT an anti-corruption layer, and the distinction is worth keeping straight. An ACL defends this
 * model from ANOTHER bounded context's model — a spreadsheet exported from a competitor's system is
 * one, and that boundary belongs to onboarding. A target is Treatment Planning's own concept that
 * happens to be stored in a shape the legacy codebase chose. That is ordinary persistence mapping.
 *
 * Two things this boundary is responsible for:
 *
 * 1. FOREIGN KEYS GAIN THEIR `Id` SUFFIX. The document says `parentTarget`; the model says
 *    `parentTargetId`. The platform standard is the suffixed form, and the one deliberate exception
 *    is the bare `client`/`clinic` the Firestore rules helpers read by name — which a target does
 *    not carry. Without this mapping the context would speak the database's vocabulary, which is how
 *    `src/domains/` became a naming convention rather than a boundary.
 * 2. THE LIFECYCLE IS ASSEMBLED. The document holds a status and four dates as five flat columns;
 *    the model holds one frozen `TargetLifecycle`. Flat is right at rest — those column names are
 *    what every existing reader and the backend already query — and nested is right in the model,
 *    where the five are one concept with rules of their own. Because they meet here, either side
 *    can change without the other.
 * 3. STAGED-EDIT MARKERS DO NOT CROSS. `editStatus` is the target editor's unsaved-changes flag: a
 *    Vue concern about a form, not a fact about a target. It stopped at this boundary the moment
 *    there was a boundary to stop at.
 *
 * Storage is unaffected. Nothing is renamed at rest — `programTargets` documents keep every field
 * name they have, and every existing reader keeps working. A context earns its vocabulary through
 * the mapper, not by rewriting the database.
 */

/**
 * `programTargets` is nested beneath its program, so a stored target does NOT carry a `program`
 * field — the identity comes from the read path, and the caller is the only one holding it. It is
 * optional because a caller that only needs the counts does not need it, and inventing one from a
 * document that has none would be worse than admitting it is absent.
 *
 * @param {Object} doc - a programTargets document
 * @param {string|null} [programId] - from the path the document was read through
 * @returns {import('../../domain/model/Target.types').Target}
 */
export function toTarget(doc, programId = null) {
  return createTarget({
    id: doc.id,
    programId,
    parentTargetId: doc.parentTarget,
    label: doc.label,
    discriminativeStimulus: doc.discriminativeStimulus,
    dataType: doc.dataType,
    order: doc.order,
    status: doc.status,
    introductionDate: doc.introductionDate,
    masteryDate: doc.masteryDate,
    holdDate: doc.holdDate,
    abortedDate: doc.abortedDate,
  });
} // toTarget()

/**
 * `programId` is deliberately not written back: it is the path the document lives at, not a field
 * on it.
 *
 * @param {import('../../domain/model/Target.types').Target} target
 * @returns {Object} document fields, without the id
 */
export function toDocument(target) {
  return {
    parentTarget: target.parentTargetId,
    label: target.label,
    discriminativeStimulus: target.discriminativeStimulus,
    dataType: target.dataType,
    order: target.order,
    // The lifecycle is one concept in the model and five columns at rest. This is the seam where
    // those two shapes meet, the way EffectivePeriod meets its two flat bounds in revenueCycle.
    status: target.lifecycle.status,
    introductionDate: target.lifecycle.introductionDate,
    masteryDate: target.lifecycle.masteryDate,
    holdDate: target.lifecycle.holdDate,
    abortedDate: target.lifecycle.abortedDate,
  };
} // toDocument()
